import React from "react";
import * as styles from "@styles/ClientsPage.css";
import { Download, Eye, Trash2 } from "lucide-react";
import type { Ok } from "@ps-bridge/ok";
import type { EmitOnAction, StateUpdate } from "@ps-bridge/capabilitiesTypes";
import type { ProjectDocument } from "@ps-bridge/models/core/dataContracts";
import EmptyView from "@view/EmptyView";
import type { IOts, OptionTs } from "@ps-bridge/interop";
import SupplyUploadStatusView from "@view/workspace/clients/documents/SupplyUploadStatus";
import type { SupplyStatusResponse } from "@services/clients/DocumentAssistantSupplyApi";

export type DocumentsPanelUploadPhase = "hidden" | "visible";
export type DocumentsPanelEmptyPhase = "hidden" | "visible";

type DocumentsPanelProps = {
  clientLabel: string;
  uploadPanelView: React.ReactElement;
  isUploadPanelOpen: boolean;
  hasActiveProject: boolean;
  supplyStatusRows: SupplyStatusResponse[];
  supplySelectedFileName: string;
  supplyErrorMessage: string;
  isSupplySubmitting: boolean;
  onSelectSupplyFile: (fileList: OptionTs<FileList>) => Promise<Ok>;
  onRefreshSupplyStatus: (supplyId: string) => Promise<Ok>;
  documentQuery: string;
  onDocumentQueryChange: StateUpdate<string>;
  documentNameOptions: string[];
  emptyQueueView: React.ReactElement;
  emptyDocumentsView: React.ReactElement;
  filteredDocuments: ProjectDocument[];
  onViewDocument: EmitOnAction<ProjectDocument>;
  onDownloadDocument: EmitOnAction<ProjectDocument>;
  onRemoveDocument: (document: ProjectDocument) => Ok;
  runIO: (effect: IOts<Ok>) => Ok;
  formatBytes: (value: number) => string;
  formatTime: (value: string) => string;
};

const DocumentsPanel: React.FC<DocumentsPanelProps> = ({
  clientLabel,
  uploadPanelView,
  isUploadPanelOpen,
  hasActiveProject,
  supplyStatusRows,
  supplySelectedFileName,
  supplyErrorMessage,
  isSupplySubmitting,
  onSelectSupplyFile,
  onRefreshSupplyStatus,
  documentQuery,
  onDocumentQueryChange,
  documentNameOptions,
  emptyQueueView,
  emptyDocumentsView,
  filteredDocuments,
  onViewDocument,
  onDownloadDocument,
  onRemoveDocument,
  runIO,
  formatBytes,
  formatTime,
}) => {
  const uploadPanelPhase: DocumentsPanelUploadPhase = isUploadPanelOpen ? "visible" : "hidden";
  const emptyDocumentsPhase: DocumentsPanelEmptyPhase =
    filteredDocuments.length === 0 ? "visible" : "hidden";
  const supplySelectedFilePhase: DocumentsPanelEmptyPhase =
    supplySelectedFileName ? "visible" : "hidden";
  const supplyErrorPhase: DocumentsPanelEmptyPhase =
    supplyErrorMessage ? "visible" : "hidden";
  const uploadView = {
    hidden: <EmptyView />,
    visible: uploadPanelView,
  } as const satisfies Record<DocumentsPanelUploadPhase, React.ReactElement>;

  const emptyDocuments = {
    hidden: <EmptyView />,
    visible: emptyDocumentsView,
  } as const satisfies Record<DocumentsPanelEmptyPhase, React.ReactElement>;
  const selectedSupplyFileView = {
    hidden: <EmptyView />,
    visible: <p className={styles.supplySelectedFile}>Selected: {supplySelectedFileName}</p>,
  } as const satisfies Record<DocumentsPanelEmptyPhase, React.ReactElement>;
  const supplyErrorView = {
    hidden: <EmptyView />,
    visible: <p role="alert" className={styles.supplyError}>{supplyErrorMessage}</p>,
  } as const satisfies Record<DocumentsPanelEmptyPhase, React.ReactElement>;
  const supplyUploadStatusView = (
    <SupplyUploadStatusView
      disabled={!hasActiveProject || isSupplySubmitting}
      selectedFileView={selectedSupplyFileView[supplySelectedFilePhase]}
      errorView={supplyErrorView[supplyErrorPhase]}
      statusRows={supplyStatusRows}
      onSelectFile={onSelectSupplyFile}
      onRefreshStatus={onRefreshSupplyStatus}
    />
  );

  return (
    <div className={styles.panelBody}>
      <div className={styles.toolbar}>
        <span className={styles.panelHint}>{clientLabel}</span>
      </div>
      <div className={`${styles.documentsLayout} ${styles.documentsLayoutSingle}`}>
        <div className={styles.documentsMain}>
          {uploadView[uploadPanelPhase]}
          {supplyUploadStatusView}
          <div className={styles.toolbar}>
            <input
              className={styles.searchInput}
              placeholder="Search documents"
              value={documentQuery}
              onChange={(event) => runIO(onDocumentQueryChange(event.target.value))}
              list="document-search-options"
            />
            <datalist id="document-search-options">
              {documentNameOptions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>
          <p className={styles.documentSearchHint}>
            Search the processing queue and completed uploads.
          </p>
          <div className={styles.documentSections}>
            <div className={styles.documentSection}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Document Processing Queue</h3>
                <p className={styles.sectionSubtitle}>
                  Files waiting to be processed.
                </p>
              </div>
              <div className={styles.list}>{emptyQueueView}</div>
            </div>

            <div className={styles.documentSection}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Processed Documents</h3>
                <p className={styles.sectionSubtitle}>
                  Successfully uploaded files.
                </p>
              </div>
              <div className={styles.list}>
                {filteredDocuments.map((doc) => (
                  <div key={doc.id} className={styles.documentRow}>
                    <div>
                      <p className={styles.listTitle}>{doc.filename}</p>
                      <p className={styles.listMeta}>
                        {formatBytes(doc.file_size)} · Added {formatTime(doc.created_at)}
                      </p>
                    </div>
                    <div className={styles.documentActions}>
                      <button
                        className={styles.ghostButton}
                        onClick={() => runIO(onViewDocument(doc))}
                      >
                        <Eye size={16} className={styles.actionIcon} />
                        View
                      </button>
                      <button
                        className={styles.ghostButton}
                        onClick={() => runIO(onDownloadDocument(doc))}
                      >
                        <Download size={16} className={styles.actionIcon} />
                        Download
                      </button>
                      <button
                        className={styles.ghostDanger}
                        onClick={() => onRemoveDocument(doc)}
                      >
                        <Trash2 size={16} className={styles.actionIcon} />
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                {emptyDocuments[emptyDocumentsPhase]}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsPanel;
